/** biome-ignore-all lint/correctness/useUniqueElementIds: expected */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { API_HOSTNAME } from '@/config';
import { ROUTES } from '@/utils/routes';
import { getJwtToken, isJwtValid, setJwtToken } from './jwt-manager';

type InviteData = {
  email: string;
  /** Set when the invited email already belongs to a registered user — they sign in instead of up. */
  _userId?: string;
  organization: { _id: string; name: string };
  inviter: { firstName?: string; lastName?: string };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_HOSTNAME}/v1${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.errors?.general?.messages?.[0] || 'Request failed');
  }

  return payload?.data as T;
}

function getSignedInEmail(): string | null {
  const jwt = getJwtToken();
  if (!isJwtValid(jwt)) return null;

  try {
    return JSON.parse(atob((jwt as string).split('.')[1])).email ?? null;
  } catch {
    return null;
  }
}

/**
 * Accepts an organization invitation on the community self-hosted build. Invitation links point at
 * `/auth/invitation/:token` — the same URL the API mails out — and the invitee may need to sign in
 * or register first, so both forms live here rather than bouncing through the auth pages.
 */
export function SelfHostedInvitationAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InviteData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const signedInEmail = getSignedInEmail();

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invitation token');

      return;
    }

    request<InviteData>(`/invites/${token}`)
      .then(setInvite)
      .catch((e: Error) => setLoadError(e.message || 'This invitation is no longer valid'));
  }, [token]);

  const acceptInvite = useCallback(async () => {
    const acceptedToken = await request<string>(`/invites/${token}/accept`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getJwtToken()}` },
    });

    if (acceptedToken) {
      setJwtToken(acceptedToken);
    }

    // Full reload so the auth shim re-reads the organization-scoped token it just received.
    window.location.href = ROUTES.ROOT;
  }, [token]);

  const handleAcceptAsCurrentUser = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await acceptInvite();
    } catch (e: any) {
      setError(e.message || 'Failed to accept the invitation');
      setIsSubmitting(false);
    }
  };

  const handleSignInAndAccept = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const data = await request<{ token: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: invite?.email, password }),
      });

      setJwtToken(data.token);
      await acceptInvite();
    } catch (e: any) {
      setError(e.message || 'Failed to sign in');
      setIsSubmitting(false);
    }
  };

  const handleSignUpAndAccept = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      // No `organizationName` — the invite provides the organization, so none is created here.
      const data = await request<{ token: string }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: invite?.email,
          password,
          firstName,
          lastName: lastName || undefined,
          invitationToken: token,
        }),
      });

      setJwtToken(data.token);
      await acceptInvite();
    } catch (e: any) {
      setError(e.message || 'Failed to create the account');
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="w-full">
        <h2 className="mb-2 text-xl font-semibold">Invitation unavailable</h2>
        <p className="mb-6 text-sm text-foreground-600">{loadError}</p>
        <Button variant="primary" mode="filled" onClick={() => navigate(ROUTES.SIGN_IN)}>
          Go to sign in
        </Button>
      </div>
    );
  }

  if (!invite) {
    return <p className="text-sm text-foreground-600">Loading invitation…</p>;
  }

  const inviterName = [invite.inviter.firstName, invite.inviter.lastName].filter(Boolean).join(' ').trim();

  return (
    <div className="w-full">
      <h2 className="mb-2 text-xl font-semibold">Join {invite.organization.name}</h2>
      <p className="mb-6 text-sm text-foreground-600">
        {inviterName ? `${inviterName} invited ` : 'You were invited as '}
        <span className="font-medium text-foreground-950">{invite.email}</span>
        {inviterName ? ` to join ${invite.organization.name}.` : '.'}
      </p>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3" role="alert">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {signedInEmail ? (
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            You are signed in as <span className="font-medium text-foreground-950">{signedInEmail}</span>. Accepting
            moves this account into {invite.organization.name}.
          </p>
          <Button
            variant="primary"
            mode="filled"
            className="w-full"
            disabled={isSubmitting}
            onClick={handleAcceptAsCurrentUser}
          >
            {isSubmitting ? 'Accepting…' : 'Accept invitation'}
          </Button>
        </div>
      ) : invite._userId ? (
        <form onSubmit={handleSignInAndAccept} className="space-y-4">
          <div>
            <label htmlFor="invite-password" className="mb-1 block text-sm font-medium text-gray-700">
              Password
            </label>
            <Input
              type="password"
              id="invite-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full"
            />
          </div>
          <Button type="submit" variant="primary" mode="filled" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in and accept'}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleSignUpAndAccept} className="space-y-4">
          <div>
            <label htmlFor="invite-first-name" className="mb-1 block text-sm font-medium text-gray-700">
              First Name <span className="text-red-600">*</span>
            </label>
            <Input
              type="text"
              id="invite-first-name"
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
              placeholder="John"
              required
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="invite-last-name" className="mb-1 block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <Input
              type="text"
              id="invite-last-name"
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
              placeholder="Doe"
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="invite-new-password" className="mb-1 block text-sm font-medium text-gray-700">
              Password <span className="text-red-600">*</span>
            </label>
            <Input
              type="password"
              id="invite-new-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Min. 8 characters, include uppercase, lowercase, number, and special character.
            </p>
          </div>
          <Button type="submit" variant="primary" mode="filled" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Creating account…' : 'Create account and join'}
          </Button>
        </form>
      )}
    </div>
  );
}
